import { useState } from 'react';
import {
  Grid, Card, CardHeader, CardContent, Divider, Stack, Box, Typography, TextField, MenuItem, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { AccountBalanceWallet, TrendingUp, Payments, ReceiptLong } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { EmptyState } from '../../components/EmptyState';
import { financeApi, type LedgerRow } from '../../api/finance.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function FinanceDashboardPage() {
  const dash = useQuery({ queryKey: ['finance-dashboard'], queryFn: financeApi.dashboard });
  const ageing = useQuery({ queryKey: ['finance-ageing', 'vendor'], queryFn: () => financeApi.ageing('vendor') });
  const reminders = useQuery({ queryKey: ['finance-reminders'], queryFn: financeApi.reminders });

  const [partyType, setPartyType] = useState('vendor');
  const [partyId, setPartyId] = useState('');
  const [ledger, setLedger] = useState<{ outstanding: number; entries: LedgerRow[] } | null>(null);

  const loadLedger = async () => {
    if (!partyId) return;
    setLedger(await financeApi.ledger(partyType, Number(partyId)));
  };

  const ageingData = ageing.data ? Object.entries(ageing.data.buckets).map(([label, value]) => ({ label, value: value as number, color: '#d97706' })) : [];

  return (
    <>
      <PageHeader title="Finance & Payments" subtitle="Payables, receivables, ageing, ledgers and payment pipeline." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#dc2626" label="Total Payable" value={inr(dash.data?.totalPayable)} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="Total Receivable" value={inr(dash.data?.totalReceivable)} icon={<ReceiptLong />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#7c3aed" label="Paid Out" value={inr(dash.data?.totalPaidOut)} icon={<Payments />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#0d9488" label="Gross Margin" value={inr(dash.data?.profitability.grossMargin)} icon={<TrendingUp />} hint="Client − Vendor invoiced" /></Grid>
      </Grid>

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard color="#d97706" label="Pending Approval" value={dash.data?.pendingApprovals ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#0284c7" label="Awaiting Verify" value={dash.data?.pendingVerification ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#1e40af" label="Awaiting Release" value={dash.data?.awaitingRelease ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#dc2626" label="Overdue" value={dash.data?.overduePayments ?? 0} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Vendor Payable Ageing" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>{ageing.data ? <MiniBarChart data={ageingData} valueFormatter={inr} /> : <EmptyState title="Loading…" dense />}</CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Reminders — awaiting action / overdue" titleTypographyProps={{ variant: 'subtitle1' }} />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {((reminders.data?.awaitingAction.length ?? 0) + (reminders.data?.overdue.length ?? 0)) === 0 ? (
                <EmptyState title="Nothing needs attention" dense />
              ) : (
                <Stack divider={<Divider />}>
                  {[...(reminders.data?.overdue ?? []).map((r) => ({ ...r, overdue: true })), ...(reminders.data?.awaitingAction ?? []).map((r) => ({ ...r, overdue: false }))].slice(0, 8).map((r) => (
                    <Box key={`${r.id}-${r.overdue}`} sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.25 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{r.code}</Typography>
                        <Typography variant="caption" color={r.overdue ? 'error.main' : 'text.secondary'}>{r.overdue ? `Overdue · due ${r.dueDate}` : r.status.replace(/_/g, ' ')}</Typography>
                      </Box>
                      <Typography variant="body2">{inr(r.totalAmount)}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Ledger Lookup" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
                <TextField select size="small" label="Party" value={partyType} onChange={(e) => { setPartyType(e.target.value); setLedger(null); }} sx={{ minWidth: 130 }}>
                  <MenuItem value="vendor">Vendor</MenuItem><MenuItem value="client">Client</MenuItem>
                </TextField>
                <TextField size="small" label="Party ID" type="number" value={partyId} onChange={(e) => setPartyId(e.target.value)} />
                <Button variant="outlined" onClick={loadLedger} disabled={!partyId}>Load Ledger</Button>
                {ledger && <Typography variant="body2">Outstanding: <b>{inr(ledger.outstanding)}</b></Typography>}
              </Stack>
              {ledger && (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Ref</TableCell><TableCell align="right">Debit</TableCell><TableCell align="right">Credit</TableCell><TableCell align="right">Balance</TableCell></TableRow></TableHead>
                  <TableBody>
                    {ledger.entries.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState title="No ledger entries" dense /></TableCell></TableRow>}
                    {ledger.entries.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.entryDate ?? '—'}</TableCell><TableCell>{e.entryType}</TableCell><TableCell>{e.referenceCode}</TableCell>
                        <TableCell align="right">{e.debit ? inr(e.debit) : '—'}</TableCell><TableCell align="right">{e.credit ? inr(e.credit) : '—'}</TableCell>
                        <TableCell align="right">{inr(e.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
